import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { login as loginApi, seedData, getMe } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Lock, Mail, Building2, Phone } from 'lucide-react';

export default function LoginPage() {
    const navigate = useNavigate();
    const { login, setTrialExpired } = useAuth();
    const [loading, setLoading] = useState(false);
    const [seeding, setSeeding] = useState(false);
    const [form, setForm] = useState({ email: '', password: '' });
    const [trialError, setTrialError] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setTrialError(false);
        try {
            const { data } = await loginApi(form);
            login(data.access_token, data.user);
            // Fetch full user info with tenant data before navigating
            try {
                const meRes = await getMe();
                login(data.access_token, meRes.data);
            } catch (meErr) {
                // If getMe fails due to trial expired, handle it
                if (meErr.response?.status === 403 && meErr.response?.data?.detail?.includes('TRIAL_EXPIRED')) {
                    setTrialError(true);
                    setLoading(false);
                    return;
                }
            }
            toast.success(`Bienvenido, ${data.user.nombre}`);
            navigate('/');
        } catch (error) {
            const detail = error.response?.data?.detail || '';
            if (detail.includes('TRIAL_EXPIRED')) {
                setTrialError(true);
            } else {
                toast.error(detail || 'Credenciales inválidas');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSeed = async () => {
        setSeeding(true);
        try {
            const { data } = await seedData();
            toast.success('Datos iniciales creados');
            setForm({ email: data.admin_email, password: data.admin_password });
        } catch (error) {
            if (error.response?.data?.message === 'Datos ya existen') {
                toast.info('Los datos ya existen. Use admin@prestamos.com / admin123');
                setForm({ email: 'admin@prestamos.com', password: 'admin123' });
            } else {
                toast.error('Error al crear datos');
            }
        } finally {
            setSeeding(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left side */}
            <div className="hidden lg:flex lg:w-1/2 relative">
                <img
                    src="https://images.unsplash.com/photo-1574848296471-28f79a036f79?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                    alt="Financial building"
                    className="object-cover w-full h-full"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-secondary/80 to-secondary/40" />
                <div className="absolute inset-0 flex items-center justify-center p-12">
                    <div className="text-white max-w-md">
                        <Building2 className="w-16 h-16 mb-6" />
                        <h1 className="font-heading text-4xl font-bold mb-4">
                            PrestaGest<span className="text-primary">SaaS</span>
                        </h1>
                        <p className="text-lg text-white/80">
                            El sistema de gestión de préstamos más completo. 
                            Control total sobre clientes, cobros y reportes.
                        </p>
                    </div>
                </div>
            </div>

            {/* Right side */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
                <Card className="w-full max-w-md border-border">
                    <CardHeader className="space-y-1">
                        <div className="flex items-center gap-2 mb-2 lg:hidden">
                            <Building2 className="w-8 h-8 text-primary" />
                            <span className="font-heading font-bold text-xl">PrestaGest</span>
                        </div>
                        <CardTitle className="font-heading text-2xl">Iniciar Sesión</CardTitle>
                        <CardDescription>
                            Ingresa tus credenciales para acceder al sistema
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {trialError && (
                            <div data-testid="trial-expired-login-error" className="mb-4 p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                                <p className="text-red-500 font-medium text-sm mb-2">Período de prueba expirado</p>
                                <p className="text-red-400/80 text-xs mb-3">Su prueba gratuita ha finalizado. Contacte para activar su plan.</p>
                                <a href="tel:7189161401" className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-500 hover:text-emerald-400">
                                    <Phone className="w-4 h-4" />
                                    718-916-1401
                                </a>
                            </div>
                        )}
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email">Correo electrónico</Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="correo@ejemplo.com"
                                        value={form.email}
                                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        className="pl-10"
                                        required
                                        data-testid="login-email-input"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password">Contraseña</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        id="password"
                                        type="password"
                                        placeholder="********"
                                        value={form.password}
                                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                                        className="pl-10"
                                        required
                                        data-testid="login-password-input"
                                    />
                                </div>
                            </div>
                            <Button
                                type="submit"
                                className="w-full"
                                disabled={loading}
                                data-testid="login-submit-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Ingresando...
                                    </>
                                ) : (
                                    'Ingresar'
                                )}
                            </Button>
                        </form>
                        <div className="mt-6 pt-6 border-t space-y-3">
                            <Link
                                to="/prueba-gratis"
                                data-testid="link-prueba-gratis"
                                className="block w-full text-center py-2.5 px-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-md text-sm font-medium transition-colors"
                            >
                                Iniciar prueba gratis de 15 días
                            </Link>
                            <Button
                                variant="outline"
                                className="w-full"
                                onClick={handleSeed}
                                disabled={seeding}
                                data-testid="seed-data-btn"
                            >
                                {seeding ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Creando...
                                    </>
                                ) : (
                                    'Crear Usuario Admin (Demo)'
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
